import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import Notification from 'components/shared/Notification';
import type { NotificationItem } from 'stores/notificationStore';
import { useNotificationActions, useNotificationStore } from 'stores/notificationStore';

export type { NotificationItem } from 'stores/notificationStore';

export const useNotifications = useNotificationActions;

const TRANSITION_TIME = 300;

const NotificationWrapper: React.FC<{
  notification: NotificationItem;
  index: number;
  previousHeights: number[];
}> = ({ notification, index, previousHeights }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isNew, setIsNew] = useState(true);
  const [isDismissing, setIsDismissing] = useState(false);
  const { removeNotification } = useNotificationActions();
  const setNotificationHeight = useNotificationStore((state) => state.setNotificationHeight);

  useEffect(() => {
    if (ref.current) {
      setNotificationHeight(notification.id, ref.current.offsetHeight);
    }
  }, [notification.id, notification.state, notification.title, setNotificationHeight]);

  useEffect(() => {
    const timer = setTimeout(() => setIsNew(false), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    notification.onDismiss?.();

    setIsDismissing(true);
    setTimeout(() => {
      removeNotification(notification.id);
    }, TRANSITION_TIME);
  };

  const topPosition = previousHeights.slice(0, index).reduce((sum, h) => sum + h + 8, 0);

  return (
    <NotificationWrapperDiv ref={ref} $isNew={isNew} $isDismissing={isDismissing} $top={topPosition}>
      <Notification
        state={notification.state}
        title={notification.title}
        timestamp={notification.timestamp}
        onActionClick={notification.onActionClick}
        onDismiss={handleDismiss}
        actionSuccessText={notification.actionSuccessText}
        actionFailureText={notification.actionFailureText}
      />
    </NotificationWrapperDiv>
  );
};

export const NotificationProvider = () => {
  const notifications = useNotificationStore((state) => state.notifications);
  const notificationHeights = useNotificationStore((state) => state.notificationHeights);

  const previousHeights = notifications.map((n) => notificationHeights[n.id] || 0);

  return (
    <NotificationContainer>
      {notifications.map((notification, index) => (
        <NotificationWrapper
          key={notification.id}
          notification={notification}
          index={index}
          previousHeights={previousHeights}
        />
      ))}
    </NotificationContainer>
  );
};

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(calc(100% + 16px));
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 9999;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const NotificationWrapperDiv = styled.div<{ $isNew: boolean; $isDismissing: boolean; $top: number }>`
  position: absolute;
  top: ${({ $top }) => $top}px;
  right: 0;
  width: max-content;
  opacity: ${({ $isDismissing }) => ($isDismissing ? 0 : 1)};
  transform: ${({ $isDismissing }) => ($isDismissing ? 'translateX(calc(100% + 16px))' : 'translateX(0)')};
  animation: ${({ $isDismissing }) => ($isDismissing ? 'none' : slideIn)} 0.3s ease-out;
  transition:
    top 0.3s ease-out,
    opacity ${TRANSITION_TIME}ms ease-out,
    transform ${TRANSITION_TIME}ms ease-out;
`;
